import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  supabase,
  isSupabaseConfigured,
  selectData,
  insertData,
} from "../lib/supabase";

const emptyItem = {
  product_name: "",
  description: "",
  quantity: 1,
  unit_price: 0,
  tax_rate: 20,
};

export default function AdminInvoices() {

  function addDays(dateString, days) {
    if (!dateString) return null;

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;

    date.setDate(date.getDate() + days);
    return date.toISOString().slice(0, 10);
  }

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [sessionUser, setSessionUser] = useState(null);
  const [message, setMessage] = useState("");
  const [createdInvoice, setCreatedInvoice] = useState(null);
  const [savedProducts, setSavedProducts] = useState([]);

  const [billingSettings, setBillingSettings] = useState({
    company_name: "",
    company_address: "",
    company_email: "",
    company_phone: "",
    beneficiary_name: "",
    iban: "",
    bic: "",
    bank_name: "",
    payment_terms: "Paiement à réception",
    payment_note: "Merci d’indiquer le numéro de facture en référence.",
  });

  const [form, setForm] = useState({
    recipient_name: "",
    recipient_email: "",
    recipient_address: "",
    issue_date: new Date().toISOString().slice(0, 10),
    currency: "EUR",
    notes: "",
    status: "draft",
  });

  const [items, setItems] = useState([{ ...emptyItem }]);

  useEffect(() => {
    initPage();
  }, []);

  async function initPage() {
    try {
      setLoading(true);
      setMessage("");

      if (!isSupabaseConfigured || !supabase) {
        setMessage(
          "Supabase n'est pas configuré. La page est visible, mais l'enregistrement est désactivé."
        );
        setIsAdmin(true);
        setLoading(false);
        return;
      }

      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) throw sessionError;

      if (!session?.user) {
        setMessage("Vous devez être connecté pour accéder à cette page.");
        setLoading(false);
        return;
      }

      setSessionUser(session.user);

      const adminResult = await selectData("app_admins", {
        filters: { user_id: session.user.id },
        limit: 1,
      });

      if (adminResult.error) throw adminResult.error;

      if (!adminResult.data || adminResult.data.length === 0) {
        setMessage("Accès refusé : cette page est réservée aux administrateurs.");
        setLoading(false);
        return;
      }

      setIsAdmin(true);

      const settingsResult = await selectData("billing_settings", {
        order: { column: "updated_at", ascending: false },
        limit: 1,
      });

      if (settingsResult.error) throw settingsResult.error;

      if (settingsResult.data?.length > 0) {
        const s = settingsResult.data[0];
        setBillingSettings({
          company_name: s.company_name || "",
          company_address: s.company_address || "",
          company_email: s.company_email || "",
          company_phone: s.company_phone || "",
          beneficiary_name: s.beneficiary_name || "",
          iban: s.iban || "",
          bic: s.bic || "",
          bank_name: s.bank_name || "",
          payment_terms: s.payment_terms || "Paiement à réception",
          payment_note:
            s.payment_note ||
            "Merci d’indiquer le numéro de facture en référence.",
        });
      }

      const productsResult = await selectData("products", {
        filters: { is_active: true },
        order: { column: "name", ascending: true },
      });

      if (productsResult.error) throw productsResult.error;

      setSavedProducts(productsResult.data || []);
    } catch (err) {
      console.error(err);
      setMessage("Erreur lors du chargement de la page admin.");
    } finally {
      setLoading(false);
    }
  }

  function updateForm(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function updateBilling(field, value) {
    setBillingSettings((prev) => ({ ...prev, [field]: value }));
  }

  function updateItem(index, field, value) {
    setItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  }

  function addItem() {
    setItems((prev) => [...prev, { ...emptyItem }]);
  }

  function removeItem(index) {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }

  function handleProductSelect(index, productId) {
    const product = savedProducts.find((p) => String(p.id) === String(productId));
    if (!product) return;

    setItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
            ...item,
            product_name: product.name || "",
            description: product.description || "",
            unit_price: product.default_unit_price || 0,
            tax_rate: product.default_tax_rate || 20,
          }
          : item
      )
    );
  }

  const totals = useMemo(() => {
    const subtotal = items.reduce((sum, item) => {
      const qty = Number(item.quantity || 0);
      const unit = Number(item.unit_price || 0);
      return sum + qty * unit;
    }, 0);

    const taxAmount = items.reduce((sum, item) => {
      const qty = Number(item.quantity || 0);
      const unit = Number(item.unit_price || 0);
      const rate = Number(item.tax_rate || 0);
      return sum + qty * unit * (rate / 100);
    }, 0);

    return {
      subtotal,
      taxAmount,
      total: subtotal + taxAmount,
    };
  }, [items]);

  async function saveBillingSettings() {
    try {
      setMessage("");

      if (!isSupabaseConfigured || !supabase) {
        setMessage("Mode démo : impossible d’enregistrer les paramètres.");
        return;
      }

      const payload = {
        company_name: billingSettings.company_name,
        company_address: billingSettings.company_address,
        company_email: billingSettings.company_email,
        company_phone: billingSettings.company_phone,
        beneficiary_name: billingSettings.beneficiary_name,
        iban: billingSettings.iban,
        bic: billingSettings.bic,
        bank_name: billingSettings.bank_name,
        payment_terms: billingSettings.payment_terms,
        payment_note: billingSettings.payment_note,
        updated_by: sessionUser?.id || null,
      };

      const result = await insertData("billing_settings", payload);

      if (result.error) throw result.error;

      setMessage("Paramètres de facturation enregistrés.");
      await initPage();
    } catch (err) {
      console.error(err);
      setMessage("Impossible d’enregistrer les paramètres de facturation.");
    }
  }

  async function createInvoice() {
    try {
      setSaving(true);
      setMessage("");
      setCreatedInvoice(null);

      if (!form.recipient_name.trim()) {
        setMessage("Le nom du destinataire est obligatoire.");
        return;
      }

      const validItems = items.filter(
        (item) =>
          item.product_name.trim() &&
          Number(item.quantity) > 0 &&
          Number(item.unit_price) >= 0
      );

      if (validItems.length === 0) {
        setMessage("Ajoute au moins un produit valide.");
        return;
      }

      if (!isSupabaseConfigured || !supabase) {
        setMessage("Mode démo : facture simulée, rien n’a été enregistré.");
        setCreatedInvoice({
          id: "demo-id",
          invoice_number: "FAC-DEMO-0001",
        });
        return;
      }

      const invoicePayload = {
        issue_date: form.issue_date,
        due_date: addDays(form.issue_date, 14),
        currency: form.currency,
        recipient_name: form.recipient_name,
        recipient_email: form.recipient_email,
        recipient_address: form.recipient_address,
        sender_name: billingSettings.company_name,
        sender_address: billingSettings.company_address,
        sender_email: billingSettings.company_email,
        sender_phone: billingSettings.company_phone,
        notes: form.notes,
        status: form.status,
        subtotal: totals.subtotal,
        tax_amount: totals.taxAmount,
        total_amount: totals.total,
        iban_snapshot: billingSettings.iban,
        bic_snapshot: billingSettings.bic,
        bank_name_snapshot: billingSettings.bank_name,
        beneficiary_name_snapshot: billingSettings.beneficiary_name,
        payment_terms_snapshot: billingSettings.payment_terms,
        payment_note_snapshot: billingSettings.payment_note,
        created_by: sessionUser?.id || null,
      };

      const { data: invoiceData, error: invoiceError } = await supabase
        .from("invoices")
        .insert([invoicePayload])
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      const itemsPayload = validItems.map((item) => {
        const qty = Number(item.quantity || 0);
        const unit = Number(item.unit_price || 0);
        const rate = Number(item.tax_rate || 0);
        const lineSubtotal = qty * unit;
        const lineTax = lineSubtotal * (rate / 100);
        const lineTotal = lineSubtotal + lineTax;

        return {
          invoice_id: invoiceData.id,
          product_name: item.product_name,
          description: item.description,
          quantity: qty,
          unit_price: unit,
          tax_rate: rate,
          line_subtotal: lineSubtotal,
          line_tax_amount: lineTax,
          line_total: lineTotal,
        };
      });

      const { error: itemsError } = await supabase
        .from("invoice_items")
        .insert(itemsPayload);

      if (itemsError) throw itemsError;

      setCreatedInvoice(invoiceData);
      setMessage(`Facture créée avec succès : ${invoiceData.invoice_number}`);
      setItems([{ ...emptyItem }]);
    } catch (err) {
      console.error(err);
      setMessage("Erreur lors de la création de la facture.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <h1 style={styles.title}>Administration des factures</h1>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <h1 style={styles.title}>Administration des factures</h1>
          <div style={styles.alertError}>{message || "Accès refusé."}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.topbar}>
          <div>
            <h1 style={styles.title}>Générateur de factures</h1>
            <p style={styles.subtitle}>
              Crée une nouvelle facture et enregistre automatiquement une échéance à 14 jours.
            </p>
          </div>

          <div style={styles.topbarActions}>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => navigate("/admin")}
            >
              Retour au dashboard
            </button>

            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => navigate("/admin/factures")}
            >
              Voir les factures
            </button>
          </div>
        </div>

        {message ? (
          <div
            style={
              message.toLowerCase().includes("succès") ||
                message.toLowerCase().includes("enregistrés")
                ? styles.alertSuccess
                : styles.alertError
            }
          >
            {message}
          </div>
        ) : null}

        <div style={styles.grid}>
          <section style={styles.card}>
            <h2 style={styles.cardTitle}>Paramètres entreprise et banque</h2>

            <div style={styles.field}>
              <label>Nom de l’entreprise</label>
              <input
                style={styles.input}
                value={billingSettings.company_name}
                onChange={(e) => updateBilling("company_name", e.target.value)}
              />
            </div>

            <div style={styles.field}>
              <label>Adresse</label>
              <textarea
                style={styles.textarea}
                rows={3}
                value={billingSettings.company_address}
                onChange={(e) => updateBilling("company_address", e.target.value)}
              />
            </div>

            <div style={styles.twoCols}>
              <div style={styles.field}>
                <label>Email</label>
                <input
                  style={styles.input}
                  value={billingSettings.company_email}
                  onChange={(e) => updateBilling("company_email", e.target.value)}
                />
              </div>

              <div style={styles.field}>
                <label>Téléphone</label>
                <input
                  style={styles.input}
                  value={billingSettings.company_phone}
                  onChange={(e) => updateBilling("company_phone", e.target.value)}
                />
              </div>
            </div>

            <div style={styles.field}>
              <label>Bénéficiaire</label>
              <input
                style={styles.input}
                value={billingSettings.beneficiary_name}
                onChange={(e) =>
                  updateBilling("beneficiary_name", e.target.value)
                }
              />
            </div>

            <div style={styles.twoCols}>
              <div style={styles.field}>
                <label>IBAN</label>
                <input
                  style={styles.input}
                  value={billingSettings.iban}
                  onChange={(e) => updateBilling("iban", e.target.value)}
                />
              </div>

              <div style={styles.field}>
                <label>BIC</label>
                <input
                  style={styles.input}
                  value={billingSettings.bic}
                  onChange={(e) => updateBilling("bic", e.target.value)}
                />
              </div>
            </div>

            <div style={styles.field}>
              <label>Banque</label>
              <input
                style={styles.input}
                value={billingSettings.bank_name}
                onChange={(e) => updateBilling("bank_name", e.target.value)}
              />
            </div>

            <div style={styles.field}>
              <label>Conditions de paiement</label>
              <input
                style={styles.input}
                value={billingSettings.payment_terms}
                onChange={(e) => updateBilling("payment_terms", e.target.value)}
              />
            </div>

            <div style={styles.field}>
              <label>Note de paiement</label>
              <textarea
                style={styles.textarea}
                rows={3}
                value={billingSettings.payment_note}
                onChange={(e) => updateBilling("payment_note", e.target.value)}
              />
            </div>

            <button style={styles.primaryButton} onClick={saveBillingSettings}>
              Enregistrer les paramètres
            </button>
          </section>

          <section style={styles.card}>
            <h2 style={styles.cardTitle}>Créer une facture</h2>

            <div style={styles.twoCols}>
              <div style={styles.field}>
                <label>Date d’émission</label>
                <input
                  type="date"
                  style={styles.input}
                  value={form.issue_date}
                  onChange={(e) => updateForm("issue_date", e.target.value)}
                />
              </div>
            </div>

            <div style={styles.field}>
              <label>Date d’échéance</label>
              <div style={styles.autoField}>
                Automatiquement calculée à 14 jours après la date d’émission
              </div>
            </div>

            <div style={styles.twoCols}>
              <div style={styles.field}>
                <label>Nom du client</label>
                <input
                  style={styles.input}
                  value={form.recipient_name}
                  onChange={(e) => updateForm("recipient_name", e.target.value)}
                />
              </div>

              <div style={styles.field}>
                <label>Email du client</label>
                <input
                  style={styles.input}
                  value={form.recipient_email}
                  onChange={(e) => updateForm("recipient_email", e.target.value)}
                />
              </div>
            </div>

            <div style={styles.field}>
              <label>Adresse du client</label>
              <textarea
                style={styles.textarea}
                rows={3}
                value={form.recipient_address}
                onChange={(e) => updateForm("recipient_address", e.target.value)}
              />
            </div>

            <div style={styles.field}>
              <label>Notes</label>
              <textarea
                style={styles.textarea}
                rows={3}
                value={form.notes}
                onChange={(e) => updateForm("notes", e.target.value)}
              />
            </div>

            <h3 style={styles.subTitle}>Produits / services</h3>

            {items.map((item, index) => (
              <div key={index} style={styles.itemBox}>
                <div style={styles.field}>
                  <label>Produit enregistré</label>
                  <select
                    style={styles.input}
                    onChange={(e) => handleProductSelect(index, e.target.value)}
                    defaultValue=""
                  >
                    <option value="">Choisir un produit</option>
                    {savedProducts.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.field}>
                  <label>Nom du produit</label>
                  <input
                    style={styles.input}
                    value={item.product_name}
                    onChange={(e) =>
                      updateItem(index, "product_name", e.target.value)
                    }
                  />
                </div>

                <div style={styles.field}>
                  <label>Description</label>
                  <textarea
                    style={styles.textarea}
                    rows={2}
                    value={item.description}
                    onChange={(e) =>
                      updateItem(index, "description", e.target.value)
                    }
                  />
                </div>

                <div style={styles.threeCols}>
                  <div style={styles.field}>
                    <label>Quantité</label>
                    <input
                      type="number"
                      min="1"
                      style={styles.input}
                      value={item.quantity}
                      onChange={(e) =>
                        updateItem(index, "quantity", e.target.value)
                      }
                    />
                  </div>

                  <div style={styles.field}>
                    <label>Prix unitaire</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      style={styles.input}
                      value={item.unit_price}
                      onChange={(e) =>
                        updateItem(index, "unit_price", e.target.value)
                      }
                    />
                  </div>

                  <div style={styles.field}>
                    <label>TVA (%)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      style={styles.input}
                      value={item.tax_rate}
                      onChange={(e) =>
                        updateItem(index, "tax_rate", e.target.value)
                      }
                    />
                  </div>
                </div>

                {items.length > 1 && (
                  <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={() => removeItem(index)}
                  >
                    Supprimer cette ligne
                  </button>
                )}
              </div>
            ))}

            <button type="button" style={styles.secondaryButton} onClick={addItem}>
              Ajouter un produit
            </button>

            <div style={styles.summary}>
              <div style={styles.summaryRow}>
                <span>Sous-total</span>
                <strong>{totals.subtotal.toFixed(2)} €</strong>
              </div>
              <div style={styles.summaryRow}>
                <span>TVA</span>
                <strong>{totals.taxAmount.toFixed(2)} €</strong>
              </div>
              <div style={styles.summaryRowTotal}>
                <span>Total TTC</span>
                <strong>{totals.total.toFixed(2)} €</strong>
              </div>
            </div>

            <button
              style={styles.primaryButton}
              onClick={createInvoice}
              disabled={saving}
            >
              {saving ? "Création..." : "Créer la facture"}
            </button>
            {createdInvoice ? (
              <div style={styles.createdBox}>
                <p><strong>Facture créée :</strong> {createdInvoice.invoice_number}</p>
                <p><strong>ID :</strong> {createdInvoice.id}</p>

                <div style={{ display: "flex", gap: "10px", marginTop: "12px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    style={styles.primaryButton}
                    onClick={() => navigate(`/admin/factures/${createdInvoice.id}`)}
                  >
                    Voir la facture
                  </button>
                </div>
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    background: "#0b1020",
    minHeight: "100vh",
    padding: "40px 20px",
    color: "#fff",
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  title: {
    fontSize: "2rem",
    fontWeight: 700,
    marginBottom: "24px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "24px",
  },
  card: {
    background: "#121933",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "18px",
    padding: "22px",
  },
  cardTitle: {
    fontSize: "1.35rem",
    marginBottom: "18px",
  },
  subTitle: {
    fontSize: "1.05rem",
    marginTop: "18px",
    marginBottom: "12px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "14px",
  },
  input: {
    marginTop: "6px",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.14)",
    background: "#0d1430",
    color: "#fff",
    outline: "none",
  },
  textarea: {
    marginTop: "6px",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.14)",
    background: "#0d1430",
    color: "#fff",
    outline: "none",
    resize: "vertical",
  },
  twoCols: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
  },
  threeCols: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "12px",
  },
  primaryButton: {
    padding: "12px 18px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    background: "#4f7cff",
    color: "#fff",
    fontWeight: 700,
    marginTop: "12px",
  },
  secondaryButton: {
    padding: "10px 14px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.12)",
    cursor: "pointer",
    background: "#1a2347",
    color: "#fff",
    fontWeight: 600,
    marginTop: "8px",
  },
  itemBox: {
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "14px",
    padding: "14px",
    marginBottom: "14px",
    background: "#0f1734",
  },
  summary: {
    marginTop: "18px",
    borderTop: "1px solid rgba(255,255,255,0.08)",
    paddingTop: "14px",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "8px",
  },
  summaryRowTotal: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "1.1rem",
    marginTop: "10px",
    paddingTop: "10px",
    borderTop: "1px solid rgba(255,255,255,0.08)",
  },
  bankPreview: {
    marginTop: "20px",
    padding: "14px",
    borderRadius: "14px",
    background: "#0d1430",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  createdBox: {
    marginTop: "18px",
    padding: "14px",
    borderRadius: "12px",
    background: "rgba(79,124,255,0.12)",
    border: "1px solid rgba(79,124,255,0.3)",
  },
  alertError: {
    padding: "12px 14px",
    borderRadius: "12px",
    background: "rgba(255, 87, 87, 0.12)",
    border: "1px solid rgba(255, 87, 87, 0.28)",
    marginBottom: "18px",
  },
  alertSuccess: {
    padding: "12px 14px",
    borderRadius: "12px",
    background: "rgba(80, 200, 120, 0.12)",
    border: "1px solid rgba(80, 200, 120, 0.28)",
    marginBottom: "18px",
  },
  autoField: {
    marginTop: "6px",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.14)",
    background: "#0d1430",
    color: "rgba(255,255,255,0.75)",
  },
  topbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "20px",
    marginBottom: "24px",
    flexWrap: "wrap",
  },
  subtitle: {
    color: "rgba(255,255,255,0.72)",
    maxWidth: "760px",
    lineHeight: 1.6,
    marginTop: "-8px",
  },
  topbarActions: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
};